import dayjs, { type Dayjs } from 'dayjs';
import minMaxPlugin from 'dayjs/plugin/minMax';
import {
  type ClaimDto,
  type Metadata,
  claimsControllerListQueryOptions,
  toKeyValueFields,
} from 'korint-sdk';
import type { ClaimsConfig } from 'korint-sdk/react';

dayjs.extend(minMaxPlugin);

type ClaimsCategory = ClaimsConfig['claimsCategories'][number];

export const claimsListQueryOptions = (
  externalId: string,
  claimsMetadata?: Record<string, string>,
) =>
  claimsControllerListQueryOptions({
    externalId,
    metadata: claimsMetadata ? { and: toKeyValueFields(claimsMetadata) as Metadata[] } : undefined,
    pageInfo: { count: 1000 }, // Pray there won't be more claims or implement pagination (requires change in how we display claims, like a proper table or list)
    order: { by: 'createdAt', direction: 'desc' },
  });

/** Claims that occurred up to this date are historical, i.e. declared at subscription */
export const getClaimsReferenceDate = (policyStartedAt?: string): Dayjs =>
  dayjs.min(dayjs(), dayjs(policyStartedAt));

export const isHistoricalClaim = (claim: Pick<ClaimDto, 'occurredAt'>, referenceDate: Dayjs) =>
  Boolean(claim.occurredAt && !referenceDate.isBefore(dayjs(claim.occurredAt)));

export const getClaimsCategoryLabel = (
  productId: string,
  { category, label }: ClaimsCategory,
  t: (key: string, options?: Record<string, unknown>) => string,
) => t(`${productId}.claims.categories.${category}`, { defaultValue: label });

/** Only the configured categories are shown, in the configured order */
export const groupClaimsByCategory = <TClaim extends Pick<ClaimDto, 'category'>>(
  claims: TClaim[],
  categories: ClaimsCategory[],
) =>
  categories.map(category => ({
    ...category,
    claims: claims.filter(claim => claim.category === category.category),
  }));

// Accelerant VTC underwriting limits, mirrored from accelerant-vtc-underwriting.service.ts
const VTC_CLAIMS_LIMITS: Array<{ reason: string; categories: string[]; max: number }> = [
  {
    reason: 'TOO_MANY_CLAIMS',
    categories: [
      'PARKED_DAMAGE',
      'THEFT',
      'FIRE',
      'GLASS_BREAKAGE',
      'MATERIAL_DAMAGE',
      'BODILY_HARM',
    ],
    max: 4,
  },
  { reason: 'TOO_MANY_MATERIAL_DAMAGE_CLAIMS', categories: ['MATERIAL_DAMAGE'], max: 3 },
  { reason: 'TOO_MANY_GLASS_BREAKAGE_CLAIMS', categories: ['GLASS_BREAKAGE'], max: 2 },
  { reason: 'TOO_MANY_BODILY_INJURY_CLAIMS', categories: ['BODILY_HARM'], max: 1 },
  { reason: 'TOO_MANY_THEFT_AND_FIRE_CLAIMS', categories: ['THEFT', 'FIRE'], max: 1 },
  { reason: 'TOO_MANY_PARKING_CLAIMS', categories: ['PARKED_DAMAGE'], max: 1 },
];

/** The VTC underwriting refusals the historical claims of each driver already trigger */
export const getVtcClaimsRefusals = ({
  claims,
  drivers,
  policyStartedAt,
}: {
  claims: Array<Pick<ClaimDto, 'category' | 'occurredAt' | 'metadata'>>;
  drivers: string[];
  policyStartedAt?: string;
}): Array<{ key: string; error: string }> => {
  const referenceDate = getClaimsReferenceDate(policyStartedAt);
  const historicalClaims = claims.filter(claim => isHistoricalClaim(claim, referenceDate));

  return drivers.flatMap(driver => {
    const driverClaims = historicalClaims.filter(({ metadata }) =>
      metadata.some(({ key, value }) => key === 'driver' && value === driver),
    );
    return VTC_CLAIMS_LIMITS.filter(
      ({ categories, max }) =>
        driverClaims.filter(({ category }) => categories.includes(category ?? '')).length > max,
    ).map(({ reason }) => ({ key: `driver${driver}`, error: reason }));
  });
};
